import { type Evaluated } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import { type EntityId, LineStyle, PointStyle, type Style, withStyle } from '@/domain/model';

import { LABEL_FONT, paintValue, type PaintContext } from './paintValue';

/**
 * The overlay layer.
 *
 * Everything here is redrawn on every frame, which is affordable precisely because it is
 * nearly empty — that is the bargain ADR-0004 strikes. Selection highlights, snap
 * indicators and in-progress tool geometry all live here so that dragging a point never
 * dirties the content layer's cached paths.
 */

export interface OverlayStyle {
  readonly selection: string;
  /** How much wider than the entity's own stroke the halo is drawn, in screen pixels. */
  readonly selectionHaloWidth: number;
  readonly selectionOpacity: number;
  readonly preview: string;
  readonly snap: string;
  readonly snapSize: number;
  /** The transform frame's outline. */
  readonly box: string;
  /**
   * The side of a drawn grip, in screen pixels.
   *
   * Its partner is `HANDLE_RADIUS_PX` in `input/tools/transformBox.ts`, which decides how near
   * the pointer must come to take hold of one. They are different quantities — a grip is
   * usually easier to hit than to see — but they must stay within sight of each other, because
   * a grip that is drawn large and picked small feels broken in a way no error message
   * explains.
   */
  readonly handleSize: number;
  readonly handleFill: string;
  /** The live figure shown beside the pointer during a gesture. */
  readonly readoutSize: number;
  readonly readoutInk: string;
  readonly readoutPlate: string;
}

export const DEFAULT_OVERLAY_STYLE: OverlayStyle = Object.freeze({
  selection: '#2563eb',
  selectionHaloWidth: 6,
  selectionOpacity: 0.32,
  preview: '#64748b',
  snap: '#ea580c',
  snapSize: 11,
  box: '#2563eb',
  handleSize: 9,
  handleFill: '#ffffff',
  readoutSize: 12,
  readoutInk: '#f8fafc',
  readoutPlate: 'rgba(15, 23, 42, 0.92)',
});

/** How the snap indicator is drawn, per snap kind. */
export const SnapMarker = {
  None: 'none',
  Grid: 'grid',
  Point: 'point',
  Curve: 'curve',
  Intersection: 'intersection',
} as const;
export type SnapMarker = (typeof SnapMarker)[keyof typeof SnapMarker];

/** A selected entity: what to draw, and the style it is drawn with on the content layer. */
export interface SelectedEntity {
  readonly value: Evaluated;
  readonly style: Style;
}

/**
 * The frame a user drags to move, turn and resize a selection.
 *
 * ## Why this carries positions rather than handle names
 *
 * The tool already knows where every grip belongs — it has to, in order to decide which one the
 * pointer is on. Sending names instead would mean this file and the tool each holding a copy of
 * the same vocabulary, kept in agreement by hand. `SnapKind` and `SnapMarker` are exactly that
 * arrangement, reconciled by a cast where the two meet, and it is a wart rather than a pattern
 * worth repeating. Points need no agreement: the painter draws where it is told.
 *
 * The rotation grip is separate from the rest because it is drawn differently — a ring on a
 * stalk rather than a square on the outline — and because it is the one grip that lies off the
 * frame, so the painter needs to know where the stalk grows from.
 */
export interface TransformBox {
  /** The four corners in world space, as a closed loop. Turned with the figure, not upright. */
  readonly corners: readonly [Vec2, Vec2, Vec2, Vec2];
  /** Resize grips, in world space. Empty draws the outline alone. */
  readonly grips: readonly Vec2[];
  /** The rotation grip and the point on the outline its stalk grows from. */
  readonly rotate: { readonly at: Vec2; readonly from: Vec2 } | null;
  /** A live figure beside the pointer while a gesture runs — `45°`, `×1.35`, `120 × 80`. */
  readonly readout: { readonly at: Vec2; readonly text: string } | null;
}

export interface OverlayInput {
  /** The selected entities, each with its own style so the halo can be sized to it. */
  readonly selected: readonly SelectedEntity[];
  /** In-progress tool geometry. */
  readonly preview: readonly Evaluated[];
  readonly snap: { readonly world: Vec2; readonly kind: SnapMarker } | null;
  /** The active tool's transform frame, when it offers one. */
  readonly box?: TransformBox | null;
  readonly style?: OverlayStyle;
}

export function paintOverlay(paint: PaintContext, input: OverlayInput): void {
  const style = input.style ?? DEFAULT_OVERLAY_STYLE;

  /*
   * Selection is drawn as a translucent halo *wider* than the entity's own stroke, not
   * as an opaque restroke in the selection colour.
   *
   * The opaque version hides the very thing the user is inspecting: change a selected
   * line's colour and nothing appears to happen, because the selection paint covers it —
   * the new colour only shows up once you click away and deselect. A halo leaves the
   * entity's real appearance visible through the middle while still reading clearly as
   * "this one is selected".
   *
   * Points already use a hollow ring, which never covered the dot, so they keep it.
   */
  for (const selected of input.selected) {
    const halo: Style = withStyle(BASE, {
      stroke: style.selection,
      strokeWidth: selected.style.strokeWidth + style.selectionHaloWidth,
      pointSize: selected.style.pointSize + style.selectionHaloWidth,
      pointStyle: PointStyle.Circle,
      opacity: style.selectionOpacity,
    });
    paintValue(paint, selected.value, halo);
  }

  const ghost: Style = withStyle(BASE, {
    stroke: style.preview,
    strokeWidth: 1.5,
    lineStyle: LineStyle.Dashed,
    pointSize: 7,
    opacity: 0.9,
  });
  for (const value of input.preview) paintValue(paint, value, ghost);

  if (input.box != null) paintTransformBox(paint, input.box, style);

  /*
   * The snap marker goes last so it is never covered by a grip. During a handle drag the two sit
   * on top of one another by design — the marker is what says the grip has landed on something
   * real — and the one carrying the information has to be the one on top.
   */
  if (input.snap !== null && input.snap.kind !== SnapMarker.None) {
    paintSnapMarker(paint, input.snap.world, input.snap.kind, style);
  }
}

/**
 * The transform frame: an outline, its grips, and whatever the gesture is currently saying.
 *
 * Everything after the first `toScreen` is in CSS pixels, which is what keeps a grip the same
 * size at every zoom without the tool recomputing anything. The overlay is repainted when the
 * camera moves, so a frame laid out here stays correct through a zoom that no pointer event
 * accompanies — which a world-space grip drawn as preview geometry would not.
 */
function paintTransformBox(paint: PaintContext, box: TransformBox, style: OverlayStyle): void {
  const { ctx } = paint;
  const corners = box.corners.map((corner) => paint.toScreen(corner));

  ctx.save();
  ctx.strokeStyle = style.box;
  ctx.lineWidth = 1;

  // The outline, dashed so it never reads as part of the drawing.
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  corners.forEach((corner, index) => {
    if (index === 0) ctx.moveTo(corner.x, corner.y);
    else ctx.lineTo(corner.x, corner.y);
  });
  ctx.closePath();
  ctx.stroke();

  ctx.setLineDash([]);

  if (box.rotate !== null) {
    const at = paint.toScreen(box.rotate.at);
    const from = paint.toScreen(box.rotate.from);

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(at.x, at.y);
    ctx.stroke();

    // A ring, not a square: the one grip that turns rather than resizes should not look like
    // the ones that resize. The same argument the snap markers make.
    ctx.fillStyle = style.handleFill;
    ctx.beginPath();
    ctx.arc(at.x, at.y, style.handleSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  ctx.fillStyle = style.handleFill;
  const side = style.handleSize;
  for (const grip of box.grips) {
    const screen = paint.toScreen(grip);
    ctx.beginPath();
    ctx.rect(screen.x - side / 2, screen.y - side / 2, side, side);
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();

  if (box.readout !== null) paintReadout(paint, box.readout.at, box.readout.text, style);
}

/**
 * The number a gesture is currently producing, beside the pointer.
 *
 * `ToolPrompt` rejected a label that follows the cursor, and was right to: it would have meant
 * tracking the pointer through React and re-rendering the shell on every mouse move, which is
 * the one cost this architecture spends real effort avoiding. Painting it here has none of that
 * cost — the overlay is already being repainted for the drag — so this gets the placement the
 * strip wanted and could not afford. It is also what makes a snapped rotation legible: the
 * figure turning is not evidence that it turned *exactly* ninety degrees, and this is.
 */
function paintReadout(paint: PaintContext, world: Vec2, text: string, style: OverlayStyle): void {
  const { ctx } = paint;
  const anchor = paint.toScreen(world);

  ctx.save();
  ctx.font = `${String(style.readoutSize)}px ${LABEL_FONT}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  const padding = 5;
  const width = ctx.measureText(text).width;
  const height = style.readoutSize + padding * 2;
  // Up and to the right, so the reading is never under the hand that is producing it.
  const left = anchor.x + 14;
  const top = anchor.y - 14 - height;

  ctx.fillStyle = style.readoutPlate;
  ctx.beginPath();
  ctx.rect(left, top, width + padding * 2, height);
  ctx.fill();

  ctx.fillStyle = style.readoutInk;
  ctx.fillText(text, left + padding, top + height / 2);

  ctx.restore();
}

const BASE: Style = Object.freeze({
  stroke: '#000000',
  strokeWidth: 1,
  lineStyle: LineStyle.Solid,
  fill: 'transparent',
  fillStyle: 'none',
  opacity: 1,
  pointStyle: PointStyle.Dot,
  pointSize: 5,
  arrows: 'none' as never,
});

/**
 * The snap indicator.
 *
 * Each snap kind gets a distinct shape rather than a distinct colour alone: the marker
 * has to tell the user *what the click will create* — a reused vertex, an intersection
 * point that will track both parents, or a locus point locked to a curve — and colour
 * alone conveys none of that, nor does it survive a colour-blind reader or a projector.
 */
function paintSnapMarker(
  paint: PaintContext,
  world: Vec2,
  kind: SnapMarker,
  style: OverlayStyle
): void {
  const { ctx } = paint;
  const screen = paint.toScreen(world);
  const r = style.snapSize / 2;

  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = style.snap;
  ctx.fillStyle = style.snap;
  ctx.lineWidth = 2;
  ctx.beginPath();

  switch (kind) {
    case SnapMarker.Point:
      // A ring around an existing vertex: "this click reuses that point".
      ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2);
      ctx.stroke();
      break;

    case SnapMarker.Intersection:
      // A cross: two things meeting.
      ctx.moveTo(screen.x - r, screen.y - r);
      ctx.lineTo(screen.x + r, screen.y + r);
      ctx.moveTo(screen.x + r, screen.y - r);
      ctx.lineTo(screen.x - r, screen.y + r);
      ctx.stroke();
      break;

    case SnapMarker.Curve:
      // A diamond riding the curve: "this point will be locked to it".
      ctx.moveTo(screen.x, screen.y - r);
      ctx.lineTo(screen.x + r, screen.y);
      ctx.lineTo(screen.x, screen.y + r);
      ctx.lineTo(screen.x - r, screen.y);
      ctx.closePath();
      ctx.stroke();
      break;

    case SnapMarker.Grid:
      // A small square, distinctly lighter than the others.
      ctx.globalAlpha = 0.6;
      ctx.rect(screen.x - r * 0.6, screen.y - r * 0.6, r * 1.2, r * 1.2);
      ctx.stroke();
      break;

    case SnapMarker.None:
      break;
  }

  ctx.restore();
}

/**
 * Collects the selected entities, skipping any with no value.
 *
 * A construction that is currently undefined has nothing to highlight, and drawing a
 * halo at the origin would be worse than drawing nothing.
 */
export function selectedValues(
  selection: Iterable<EntityId>,
  lookup: (id: EntityId) => { value: Evaluated | null; style: Style } | null
): readonly SelectedEntity[] {
  const result: SelectedEntity[] = [];
  for (const id of selection) {
    const found = lookup(id);
    if (found?.value == null) continue;
    result.push({ value: found.value, style: found.style });
  }
  return result;
}
